import { useMemo, useState, useEffect, useRef } from "react"
import styles from './index.module.less'
import { Button } from "antd"
import TableSearch, { ConfigItem } from "@/components/TableSearch"
import { FieldTypeEnum } from "@/config/enum"
import { ReactComponent as PlusIcon } from '@/assets/svg/plus.svg'
import { useAuth } from "@/hooks/useAuth"
import { DocSearchCodes } from "../../constants"
import { getDictListByTemplate } from "@/pages/documentCompile/api/dict"
import { DictTemplateCodeEnum, DocStatusOptions } from "@/pages/documentCompile/dict/type"
import { filterEmptyValue } from "@/components/TableSearch/utils"
import { useDebounceFn } from "ahooks"
import { ReloadOutlined } from "@ant-design/icons"
import { getOrgList } from "@/api"
import { getStorage } from "@/utils/tools"
import { traverseTree } from "@/utils/tree"

const Index = ({
  onCreate,
  onSearch,
  loading: _loading,
}: {
  onCreate: () => void;
  onSearch: (params) => void;
  loading: boolean;
}) => {
  const { hasAuth, codes } = useAuth()
  const authNew = useMemo(() => hasAuth('630:doc:prep:proj:add'), [codes])
  const lastSearchParamsRef = useRef<Record<string, any>>({})

  const [projectTypeOptions, setProjectTypeOptions] = useState<any[]>([])
  const [projectStatusOptions, setProjectStatusOptions] = useState<any[]>([])
  const [docStatusOptions, setDocStatusOptions] = useState<any[]>([])
  const [searchSchema, setSearchSchema] = useState<ConfigItem[]>([])
  const [orgList, setOrgList] = useState<any[]>([]) // 组织结构
  const formatDictOptions = (res: any) => res?.data || res || []

  const normalizeOrgTreeForTreeSelect = (nodes: any[] = []) => {
    const sorted = [...(nodes || [])].sort((a, b) => (a?.sort ?? 0) - (b?.sort ?? 0))
    const tree = sorted.map(node => {
      const children = Array.isArray(node?.children) ? node.children : []
      return {
        ...node,
        key: node?.nodeId ?? node?.id ?? node?.key,
        title: node?.nodeName ?? node?.name ?? node?.title,
        value: node?.nodeId ?? node?.id ?? node?.value,
        children: children.length > 0 ? normalizeOrgTreeForTreeSelect(children) : undefined,
      }
    })

    // 叶子节点去掉 children 字段，避免出现“空 children”导致交互怪异
    traverseTree(tree, n => {
      const hasChildren = Array.isArray(n?.children) && n.children.length > 0
      n.isLeaf = !hasChildren
      if (!hasChildren) {
        delete n.children
      }
    })

    return tree
  }

  const getEnumOptions = (dicts: any[], dictCode: string) => {
    const target = dicts.find(item => item.dictCode === dictCode)
    const attrs = target?.extraAttrs || []
    const enumAttr = attrs.find(attr => attr.attrCode === 'nr')
    return enumAttr?.attrVals?.map(val => ({
      label: val.value,
      value: val.key
    })) || []
  }

  const loadDictOptions = async () => {
    try {
      const [projectTypeRes, projectAttrRes, orgListRes] = await Promise.all([
        getDictListByTemplate({ templateCode: DictTemplateCodeEnum.ENG_CLASS }),
        getDictListByTemplate({ templateCode: DictTemplateCodeEnum.PROJECT_ATTR }),
        getOrgList({
          tenantId: getStorage('tenantId'),
        }),
      ])
      const projectTypeList = formatDictOptions(projectTypeRes)
      const projectAttrList = formatDictOptions(projectAttrRes)
      setProjectTypeOptions(projectTypeList.map(item => ({
        label: item.dictName,
        value: item.dictCode
      })))
      setProjectStatusOptions(getEnumOptions(projectAttrList, DocSearchCodes.projectStatus))
      // 资料状态 1:编制中 2:已完成	
      setDocStatusOptions(DocStatusOptions)

      // 组织结构
      const orgTree = normalizeOrgTreeForTreeSelect(Array.isArray(orgListRes) ? orgListRes : [])
      setOrgList(orgTree || [])
    } catch (error) {
      console.error('加载搜索下拉数据失败', error)
    }
  }

  useEffect(() => {
    loadDictOptions()
  }, [])
  const searchSchemaData: ConfigItem[] = useMemo(() => ([
    {
      label: '资料包名称',
      key: DocSearchCodes.projectName,
      name: DocSearchCodes.projectName,
      placeholder: '请输入资料包名称',
      width: 220,
      allowClear: true,
      componentType: FieldTypeEnum.INPUT,
    },
    {
      label: '工程名称',
      key: DocSearchCodes.engName,
      name: DocSearchCodes.engName,
      placeholder: '请输入工程名称',
      width: 220,
      allowClear: true,
      componentType: FieldTypeEnum.INPUT,
    },
    {
      label: '工程编号',
      key: DocSearchCodes.engCode,
      name: DocSearchCodes.engCode,
      placeholder: '请输入工程编号',
      width: 220,
      allowClear: true,
      componentType: FieldTypeEnum.INPUT,
    },
    {
      label: '工程类型',
      key: DocSearchCodes.projectType,
      name: DocSearchCodes.projectType,
      placeholder: '请选择工程类型',
      width: 220,
      allowClear: true,
      componentType: FieldTypeEnum.SELECT,
      options: projectTypeOptions,
    },
    {
      label: '工程状态',
      key: DocSearchCodes.projectStatus,
      name: DocSearchCodes.projectStatus,
      placeholder: '请选择工程状态',
      width: 220,
      allowClear: true,
      componentType: FieldTypeEnum.SELECT,
      options: projectStatusOptions,
    },
    {
      label: '资料状态',
      key: DocSearchCodes.docStatus,
      name: DocSearchCodes.docStatus,
      placeholder: '请选择资料状态',
      width: 220,
      allowClear: true,
      componentType: FieldTypeEnum.SELECT,
      options: docStatusOptions,
    },
    {
      label: '开工日期',
      key: DocSearchCodes.startDate,
      name: DocSearchCodes.startDate,
      width: 320,
      componentType: FieldTypeEnum.DATETIME_RANGE,
      picker: 'date',
    },
    {
      label: '竣工日期',
      key: DocSearchCodes.endDate,
      name: DocSearchCodes.endDate,
      width: 320,
      componentType: FieldTypeEnum.DATETIME_RANGE,
      picker: 'date',
    },
    // 部门组织
    {
      label: '部门组织',
      key: DocSearchCodes.orgIds,
      name: DocSearchCodes.orgIds,
      width: 220,
      componentType: FieldTypeEnum.TREESELECT,
      options: orgList,
      selectConfig: {
        treeCheckable: false, // 禁用复选框，实现单选
        multiple: false,      // 禁用多选
        allowClear: true,
        fieldNames: {
          label: 'title',
          value: 'value',
          children: 'children'
        }
      }
    },
  ]), [projectTypeOptions, projectStatusOptions, docStatusOptions, orgList])

  useEffect(() => {
    setSearchSchema(searchSchemaData)
  }, [searchSchemaData])

  const handleSearch = async formData => {
    lastSearchParamsRef.current = formData || {}
    onSearch(formData)
  }

  const { run: runInputSearch } = useDebounceFn(
    (formData) => {
      handleSearch(filterEmptyValue(formData))
    },
    {
      wait: 300,
    },
  )

  const handleNew = () => {
    onCreate()
  }

  const handleRefresh = () => {
    handleSearch(lastSearchParamsRef.current || {})
  }

  const searchOps = [
    {
      key: '1',
      children: (
        <Button disabled={!authNew} className={styles.createBtn} type='primary' onClick={handleNew}>
          <div className={styles.btnContainer}><PlusIcon className={styles.plusIcon} />新建资料工程</div>
        </Button>
      ),
    },
    {
      key: 'refresh',
      position: 'right',
      children: (
        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={_loading}>
          刷新
        </Button>
      ),
    },
  ]

  const handleTableSearchFormChange = (changedValues, allValues) => {
    const changeKey = Object.keys(changedValues || {})[0]
    if (!changeKey) return
    const targetField = searchSchema.find(item => item.key === changeKey)
    if (targetField?.componentType === FieldTypeEnum.INPUT) {
      runInputSearch(allValues)
    }
    if (targetField?.componentType === FieldTypeEnum.TREESELECT) {
      runInputSearch(allValues)
    }
  }

  return (
    <div className={styles.container}>
      <TableSearch
        className={styles.tableSearch}
        ops={searchOps}
        showTag={false}
        config={searchSchema}
        onChange={handleTableSearchFormChange}
        needMore={true}
        showCount={3}
        onSearch={handleSearch}
        groupInputFields={false}
      />
    </div>
  )
}

export default Index
